import React, {forwardRef, memo, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {useMergeRefs} from '@chakra-ui/react'
import {ButtonGroup as ChakraButtonGroup, useSlotRecipe} from '@chakra-ui/react3'
import {useNonce} from '../../../../hooks'
import {buttonGroupSlotRecipe} from './index.recipe'

const IS_TEST = process.env.NODE_ENV === 'test'

// DO NOT USE MODERN JS INSIDE OR REFERENCE ANYTHING OUTSIDE OF THIS FUNCTION
// This function is stringified and embedded in a script tag for SSR.
// Modern JS features may not be supported in all browsers, and external references
// will be undefined in the inline script context.
function preHydrationWrapDetector() {
    const c = document.currentScript.previousElementSibling
    const b = [...c.children]
        // chakra during ssr renders the styles in a style tag as sibling to the component
        // we need to filter them out to avoid counting them as visible children
        .filter((child) => child.tagName.toUpperCase() !== 'STYLE')
    if (b.length < 2) return

    const f = () => {
        c.dataset.wrapped = b[0].offsetTop !== b[b.length - 1].offsetTop
    }
    const observer = new ResizeObserver(f)
    observer.observe(c)
    f()

    // store observer for cleanup
    c._wrapObserver = observer
}

/**
 * Button group component with responsive wrapping detection.
 *
 * Renders a Chakra v3 `ButtonGroup` and applies slot-recipe styles based on the
 * provided orientation. It also detects when child elements wrap onto multiple
 * lines and sets a `data-wrapped` attribute on the container for styling.
 * If the buttons wrap onto multiple lines, they expand to 100% of the available container width.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - The button elements to be grouped.
 * @param {'horizontal'|'vertical'} [props.orientation='horizontal'] - Layout direction of the button group.
 * @param {'true'|'false'} [props.canWrap='false'] - Controls whether wrapping styles are enabled through the slot recipe.
 * @param {React.Ref} ref - Ref to the container element.
 * @returns {JSX.Element} Rendered ButtonGroup element.
 */
const ButtonGroup = memo(
    forwardRef(function ButtonGroup(
        {orientation = 'horizontal', canWrap = 'false', ...props},
        ref
    ) {
        const nonce = useNonce()

        const containerRef = useRef(null)
        const mergedRef = useMergeRefs(ref, containerRef)

        const recipe = useSlotRecipe({recipe: buttonGroupSlotRecipe})
        const styles = useMemo(
            () =>
                recipe({
                    orientation,
                    canWrap
                }),
            [recipe, orientation, canWrap]
        )

        // wrap detection using ResizeObserver to add data-wrapped attribute to container
        useEffect(() => {
            const container = containerRef.current
            if (!container) return

            // destroy the observer from the ssr embedded script if it exists, to use the one created here
            if (container._wrapObserver) {
                container._wrapObserver.disconnect()
                delete container._wrapObserver
            }

            const containerChildren = container.children
            if (containerChildren.length < 2) return

            let lastWrapped = false
            const checkForWrapping = () => {
                const wrapped =
                    containerChildren[0].offsetTop !==
                    containerChildren[containerChildren.length - 1].offsetTop
                if (wrapped !== lastWrapped) {
                    container.dataset.wrapped = wrapped
                    lastWrapped = wrapped
                }
            }

            const observer = new ResizeObserver(checkForWrapping)
            observer.observe(container)
            checkForWrapping()

            return () => {
                observer.disconnect()
            }
        }, [])

        return (
            <>
                <ChakraButtonGroup {...props} ref={mergedRef} {...styles.container} />
                {
                    // DO NOT INCLUDE THIS IN TESTS as it crashes istanbul coverage
                    // with "ReferenceError: cov_233hcnig72 is not defined" because we
                    // dynamically create a new function in the script tag
                    !IS_TEST && (
                        // inline script to detect wrapping of buttons as soon as the component is rendered
                        // only used for ssr until hydration
                        <script
                            lang="javascript"
                            type="text/javascript"
                            nonce={nonce}
                            dangerouslySetInnerHTML={{
                                __html: `(${preHydrationWrapDetector.toString()})()`
                            }}
                        />
                    )
                }
            </>
        )
    })
)

if (process.env.NODE_ENV !== 'production') {
    ButtonGroup.displayName = 'ButtonGroup'
}

ButtonGroup.propTypes = {
    children: PropTypes.node,
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    canWrap: PropTypes.bool
}

export default ButtonGroup
